/**
 * Jobs List View
 *
 * Table view for jobs with multi-select capabilities.
 * PURELY PRESENTATIONAL - all data comes from props.
 *
 * DDD Compliance:
 * - Uses JobCardViewModel from application layer
 * - No business logic in component
 * - Displays view model data only
 */

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import type { JobCardViewModel } from '@/application/service-provider/view-models/JobListViewModel';

interface JobsListViewProps {
  jobs: JobCardViewModel[];
  selectedJobIds: Set<string>;
  onToggleSelection: (jobId: string) => void;
  onToggleAll: () => void;
  onViewDetails: (jobId: string) => void;
}

export function JobsListView({
  jobs,
  selectedJobIds,
  onToggleSelection,
  onToggleAll,
  onViewDetails,
}: JobsListViewProps) {
  const allSelected = jobs.length > 0 && jobs.every(j => selectedJobIds.has(j.id));
  const someSelected = jobs.some(j => selectedJobIds.has(j.id)) && !allSelected;

  if (jobs.length === 0) {
    return (
      <div className="card">
        <div className="card-content text-center py-12">
          <p className="text-neutral-600">No jobs to display</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-12">
              <input
                type="checkbox"
                checked={allSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someSelected;
                }}
                onChange={onToggleAll}
                className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                aria-label="Select all jobs"
              />
            </TableHead>
            <TableHead>Job #</TableHead>
            <TableHead>Customer</TableHead>
            <TableHead>Address</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Scheduled</TableHead>
            <TableHead>Resources</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {jobs.map(job => (
            <TableRow
              key={job.id}
              data-state={selectedJobIds.has(job.id) ? 'selected' : undefined}
            >
              <TableCell>
                <input
                  type="checkbox"
                  checked={selectedJobIds.has(job.id)}
                  onChange={() => onToggleSelection(job.id)}
                  className="w-4 h-4 rounded border-neutral-300 text-primary-600 focus:ring-primary-500"
                  aria-label={`Select job ${job.displayName}`}
                />
              </TableCell>
              <TableCell className="font-medium">{job.displayName}</TableCell>
              <TableCell>
                <div className="text-sm text-neutral-900">
                  {job.displayName}
                </div>
              </TableCell>
              <TableCell className="text-sm text-neutral-600 max-w-xs truncate">
                {job.displayAddress}
              </TableCell>
              <TableCell>
                <StatusBadge badge={job.statusBadge} />
                {job.urgencyLevel !== 'normal' && (
                  <div className="mt-1">
                    <UrgencyIndicator level={job.urgencyLevel} />
                  </div>
                )}
              </TableCell>
              <TableCell className="text-sm">
                <div>{job.scheduleText}</div>
                {job.targetCompletionDate && (
                  <div className="text-xs text-neutral-600 mt-1">
                    Target: {job.targetCompletionDate}
                  </div>
                )}
              </TableCell>
              <TableCell>
                <ResourceBadges
                  container={job.assignedContainer}
                  vehicle={job.assignedVehicle}
                  driver={job.assignedDriver}
                />
              </TableCell>
              <TableCell className="text-right">
                <button
                  onClick={() => onViewDetails(job.id)}
                  className="text-sm text-primary-600 hover:text-primary-700 hover:underline font-medium"
                >
                  View Details
                </button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

// Helper Components

function StatusBadge({
  badge,
}: {
  badge: { label: string; color: string; icon: string };
}) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-800',
    green: 'bg-green-100 text-green-800',
    yellow: 'bg-yellow-100 text-yellow-800',
    red: 'bg-red-100 text-red-800',
    gray: 'bg-gray-100 text-gray-800',
  }[badge.color] || 'bg-gray-100 text-gray-800';

  return (
    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${colorClasses}`}>
      {badge.label}
    </span>
  );
}

function UrgencyIndicator({ level }: { level: 'normal' | 'soon' | 'urgent' }) {
  if (level === 'normal') return null;

  const config = {
    soon: {
      label: 'Due Soon',
      className: 'bg-yellow-50 text-yellow-700 border-yellow-200',
    },
    urgent: {
      label: 'Overdue',
      className: 'bg-red-50 text-red-700 border-red-200',
    },
  };

  const { label, className } = config[level];

  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${className}`}>
      {label}
    </span>
  );
}

function ResourceBadges({
  container,
  vehicle,
  driver,
}: {
  container: { id: string; containerNumber: string; displayName: string } | undefined;
  vehicle: { id: string; vehicleNumber: string; displayName: string } | undefined;
  driver: { id: string; name: string } | undefined;
}) {
  const resources = [
    container && { type: 'Container', name: container.displayName },
    vehicle && { type: 'Vehicle', name: vehicle.displayName },
    driver && { type: 'Driver', name: driver.name },
  ].filter(Boolean);

  if (resources.length === 0) {
    return <span className="text-xs text-neutral-400 italic">Not assigned</span>;
  }

  return (
    <div className="flex flex-col gap-1">
      {resources.map((resource, idx) => (
        <div key={idx} className="text-xs">
          <span className="text-neutral-600">{resource!.type}:</span>{' '}
          <span className="font-medium text-neutral-900">{resource!.name}</span>
        </div>
      ))}
    </div>
  );
}
